import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  SystemConfigEditModal,
  SystemConfigViewModal,
} from 'components/modals/modules/utility-module/SystemConfigModal';
import type {
  SystemConfigFormData,
  SystemConfigRecord,
} from 'components/modals/modules/utility-module/SystemConfigModal';
import { formatDate } from 'utils/lmmsHelpers';
import { useSystemConfig, useUpdateSystemConfig } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// INITIAL FORM DATA
// ============================================================================

const emptyFormData: SystemConfigFormData = {
  configValue: '',
  description: '',
};

// ============================================================================
// MAIN SYSTEM CONFIGURATION SCREEN COMPONENT
// ============================================================================

const SystemConfigScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useSystemConfig();
  const { updateConfig } = useUpdateSystemConfig();
  // Data from API
  const configs = (apiData as unknown as SystemConfigRecord[]) || [];
  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<SystemConfigRecord | null>(null);
  const [formData, setFormData] = useState<SystemConfigFormData>(emptyFormData);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() =>
    configs.filter((cfg) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        cfg.configKey.toLowerCase().includes(q) ||
        cfg.configValue.toLowerCase().includes(q) ||
        cfg.description.toLowerCase().includes(q) ||
        cfg.updatedBy.toLowerCase().includes(q)
      );
    }),
    [configs, searchQuery]
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleEdit = useCallback((record: SystemConfigRecord) => {
    setSelectedRecord(record);
    setFormData({
      configValue: record.configValue,
      description: record.description,
    });
    setEditModalOpen(true);
  }, []);

  const handleView = useCallback((record: SystemConfigRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(() => {
    if (!formData.configValue.trim()) {
      showMessage('Config Value is required', 'error');
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Save Configuration',
      message: `Are you sure you want to update "${selectedRecord?.configKey}" to "${formData.configValue}"?`,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          if (selectedRecord) {
            await updateConfig(selectedRecord.id, {
                id: selectedRecord.id,
                configValue: formData.configValue.trim(),
              });
              await refetch();

            showMessage('Configuration updated successfully', 'success');
          }
          setEditModalOpen(false);
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || 'Failed to update configuration', 'error');
        }
      },
    });
  }, [formData, selectedRecord, showMessage, updateConfig, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: SystemConfigRecord): ActionMenuItem[] => [
    {
      id: 'view',
      label: 'View Details',
      icon: <IconSystem.eye size={18} />,
      onClick: () => handleView(row),
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={18} />,
      onClick: () => handleEdit(row),
    },
  ], [handleView, handleEdit]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<SystemConfigRecord>[] = useMemo(() => [
    {
      id: 'configKey',
      header: 'Config Key',
      accessorKey: 'configKey',
      size: 220,
      cell: ({ getValue }: any) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}>
          {getValue()}
        </span>
      ),
    },
    {
      id: 'configValue',
      header: 'Config Value',
      accessorKey: 'configValue',
      size: 150,
      cell: ({ getValue }: any) => (
        <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
          {getValue()}
        </span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      size: 350,
      cell: ({ getValue }: any) => (
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          {getValue()}
        </span>
      ),
    },
    { id: 'updatedBy', header: 'Updated By', accessorKey: 'updatedBy', size: 140 },
    {
      id: 'updatedAt',
      header: 'Updated At',
      accessorKey: 'updatedAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE OPTIONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    {
      id: 'list',
      icon: <IconSystem.list size={16} />,
      label: 'List View',
      tooltip: 'List View'
    },
    {
      id: 'grid',
      icon: <IconSystem.grid size={16} />,
      label: 'Grid View',
      tooltip: 'Grid View',
      disabled: true
    },
    {
      id: 'calendar',
      icon: <IconSystem.calendar size={16} />,
      label: 'Calendar View',
      tooltip: 'Calendar View (Not Applicable)',
      disabled: true
    }
  ];

  // ============================================================================
  // ACTION BUTTONS (no Add — edit only)
  // ============================================================================

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="System Configuration"
        icon={<IconSystem.settings size={20} />}
        description="Manage system-wide configuration parameters for LMMS calculations and workflows."
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search configuration...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && configs.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load configuration</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && configs.length === 0 ? (
          <GridLoadingSkeleton columns={4} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enablePagination
              enableColumnFilters={false}
              rowActions={getRowActions}
              emptyMessage="No configuration parameters found"
            />
          </div>
        </div>
        )}
      </div>

      {/* Edit Modal */}
      <SystemConfigEditModal
        isOpen={editModalOpen}
        record={selectedRecord}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onClose={() => setEditModalOpen(false)}
      />

      {/* View Modal */}
      <SystemConfigViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel="Save"
      />
    </div>
  );
};

export default SystemConfigScreen;
